import type { FormRecognitionConfig } from '@capture/shared';
import { HintsEditor } from './HintsEditor';
import { SamplesUploader } from './SamplesUploader';
import { ThresholdBlock } from './ThresholdBlock';
import './recognition.css';

interface Props {
  config: FormRecognitionConfig;
  onChange: (next: FormRecognitionConfig) => void;
  onSampleUpload: (file: File) => void;
  onSampleDelete: (id: string) => void;
  /**
   * F-044 — the job's classification mode. When `off`, these recognition
   * settings have no runtime effect (documents route to the default form),
   * so we surface a banner instead of silently letting the admin author
   * config that does nothing. Undefined while the job type is still loading
   * (no banner shown until we know the real value).
   */
  classificationMode?: string;
}

export function RecognitionTab({
  config,
  onChange,
  onSampleUpload,
  onSampleDelete,
  classificationMode,
}: Props) {
  return (
    <div className="recognition-tab">
      {classificationMode === 'off' && (
        <div className="recognition-off-banner" role="status">
          <strong>Classification is Off for this job.</strong> These recognition
          settings won&apos;t take effect — every document routes to the default
          form. Set <em>Classification mode</em> to Learned or Source declared in{' '}
          <strong>Job Config → General</strong> to use them.
        </div>
      )}
      <HintsEditor
        hints={config.hints}
        onChange={(hints) => onChange({ ...config, hints })}
      />
      <SamplesUploader
        samples={config.sampleTemplates}
        onUpload={onSampleUpload}
        onDelete={onSampleDelete}
      />
      <ThresholdBlock
        threshold={config.confidenceThreshold}
        policy={config.multiFormBatchPolicy}
        onChangeThreshold={(t) =>
          onChange({ ...config, confidenceThreshold: t })
        }
        onChangePolicy={(p) =>
          onChange({ ...config, multiFormBatchPolicy: p })
        }
      />
    </div>
  );
}
